/**
 * AppIcon — per-app brand glyphs built from the K-OS "JC" logo frame.
 *
 * Each app icon reuses the logo's three concentric strokes (black C, grey J,
 * grey continuation) as a shared frame, with a red symbol inside that names
 * the app: a check (Tasks), a pulse (Health), and a bar chart (Finance).
 *
 * Geometry is ported verbatim from the source SVGs (360×360 viewBox,
 * 36px frame strokes, 30px symbol strokes). Frame colours are theme-aware
 * via AppIcon.module.css; the red symbols are constant #FF0000.
 */

import type { ReactNode } from 'react';
import type { AppSlug } from '@k-os/core';
import styles from './AppIcon.module.css';

const RED = '#FF0000';

/** Shared JC frame — identical across every app icon. */
function Frame() {
  return (
    <g fill="none" strokeWidth="36" strokeLinecap="round">
      <path
        className={styles.grey}
        d="M 275.968 82.138 A 138.500 138.500 0 0 1 318.163 172.339"
      />
      <path
        className={styles.j}
        d="M 318.500 43 L 318.500 182.000 A 138.500 138.500 0 0 1 275.968 281.862"
        strokeLinejoin="round"
      />
      <path
        className={styles.c}
        d="M 240.714 57.517 A 138.500 138.500 0 1 0 240.714 306.483"
      />
    </g>
  );
}

const SYMBOLS: Record<AppSlug, ReactNode> = {
  tasks: (
    <path
      d="M 112 183 L 154 225 L 235 144"
      fill="none"
      stroke={RED}
      strokeWidth="30"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  health: (
    <path
      d="M 91 191 L 126 191 L 150 143 L 183 231 L 210 174 L 232 191 L 270 191"
      fill="none"
      stroke={RED}
      strokeWidth="30"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  finance: (
    <g fill="none" strokeWidth="30" strokeLinecap="round">
      <path className={styles.grey} d="M 121 236 L 121 205" />
      <path className={styles.c} d="M 176 236 L 176 170" />
      <path stroke={RED} d="M 231 236 L 231 132" />
    </g>
  ),
};

export interface AppIconProps {
  slug: AppSlug;
  size?: number;
  className?: string;
  title?: string;
}

export function AppIcon({ slug, size = 30, className, title }: AppIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 360 360"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <Frame />
      {SYMBOLS[slug]}
    </svg>
  );
}
